import React, { useMemo } from "react";
import { FiltersTypes } from "@common/types/filters/general";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { pluralize } from "@common/helpers/string";

interface Props {
    filters: Pick<
        FiltersTypes,
        | "engagementTeamRoles"
        | "engagementTeamRoleOperator"
        | "engagementTeamMemberIds"
        | "engagementTeamMemberOperator"
    >;
}

const ROLE_LABELS: Record<string, string> = {
    preparer: "Preparer",
    reviewer: "Reviewer",
    manager: "Manager",
};

export const EngagementTeamFilterLabel: React.FC<Props> = ({ filters }) => {
    const { data: orgMembers } = useCurrentOrganizationMembers();

    const { engagementTeamRoles, engagementTeamMemberIds } = filters;

    const memberNames = useMemo(
        () =>
            (orgMembers ?? [])
                .filter((m) => engagementTeamMemberIds?.includes(m.user.id))
                .map((m) => m.user.name),
        [orgMembers, engagementTeamMemberIds],
    );

    const roleCount = engagementTeamRoles?.length ?? 0;
    const memberCount = memberNames.length;

    if (roleCount > 0 && memberCount > 0) {
        return (
            <>
                {roleCount} {pluralize("role", roleCount)}, {memberCount}{" "}
                {pluralize("member", memberCount)}
            </>
        );
    }

    if (roleCount > 0) {
        if (roleCount === 1 && engagementTeamRoles) {
            const label =
                ROLE_LABELS[engagementTeamRoles[0]] ?? engagementTeamRoles[0];
            return <>{label}</>;
        }
        return (
            <>
                {roleCount} {pluralize("role", roleCount)}
            </>
        );
    }

    if (memberCount > 0) {
        if (memberCount === 1) {
            return <>{memberNames[0]}</>;
        }
        return (
            <>
                {memberCount} {pluralize("member", memberCount)}
            </>
        );
    }

    return <>Engagement team</>;
};
